/**
 * Стартовое предложение набора тем (#845, PR 4; спека
 * docs/plans/2026-10-02-conversation-themes.md §2 Р1, §4) — чистая часть:
 * пары истории, отбор фраз для модели и доли тем. Одна реализация для
 * workflows-api (строит предложение и зовёт модель) и edge-api (показывает
 * доли на экране утверждения): доли на экране обязаны считаться по тем же
 * парам и тем же правилам, по которым фразы ушли в модель.
 *
 * **Пара** — ключ `(нормализованный topic, call_category)`. Нормализация —
 * `normalizeThemeKey`, та же, что у `norm_key` тем: SQL отдаёт сырые строки,
 * сведение живёт только в JS (`normalize.ts`: два места нормализации
 * расходятся молча). Одна фраза в разных категориях — разные пары и разные
 * номера: разговор одной категории не попадает в тему, выбранную для другой.
 *
 * **Доли — по разговорам.** У разговора один текущий анализ (`LIMIT 1` в
 * `CURRENT_ANALYSIS_JOIN`) и одна категория, значит ровно одна пара: группы
 * истории не пересекаются по разговорам, и сумма их счётов по парам темы —
 * это `COUNT(DISTINCT record)` разговоров темы. Объединение тем считается по
 * множеству номеров, а не суммой тем: фраза в двух темах — в обеих, но
 * разговор в «покрытых темами» — один раз.
 *
 * Без импорта значений из `@aica/api-types` (кольцо core ↔ api-types, #1076):
 * константы — здесь, контракт Zod берёт их отсюда.
 */
import { normalizeThemeKey } from './theme-key';
import { THEME_OVERLAP_CALL_CATEGORY_TERMS } from './theme-overlap';

/** Окно истории — последние 90 дней (§2 Р1). */
export const THEME_BOOTSTRAP_WINDOW_DAYS = 90;

/**
 * Окно истории предложения, полуоткрытое `[start, end)`, на момент `now`:
 * 90 дней назад. Одно место для воркера (строит предложение) и edge-api
 * (считает, есть ли из чего предлагать): окна не должны расходиться.
 */
export function themeBootstrapWindow(now: Date): { start: Date; end: Date } {
  return {
    start: new Date(now.getTime() - THEME_BOOTSTRAP_WINDOW_DAYS * 24 * 60 * 60 * 1000),
    end: now,
  };
}

/** Потолок фраз одного вызова (§2 Р1). */
export const THEME_BOOTSTRAP_MAX_PHRASES = 1_500;

/** Потолок знаков блока фраз одного вызова (§2 Р1) — по строкам `lineLength`. */
export const THEME_BOOTSTRAP_MAX_CHARS = 60_000;

/** Длина одной фразы в промпте: `topic` — «до 10 слов», запас на длинные слова. */
export const THEME_BOOTSTRAP_PHRASE_MAX_CHARS = 120;

/** Сколько тем просит предложение (§2 Р1): 10–25. */
export const THEME_BOOTSTRAP_MIN_THEMES = 10;
export const THEME_BOOTSTRAP_MAX_THEMES = 25;

/**
 * Окна захвата попытки (шапка миграции 202610082305_org_themes.sql):
 * `failed` перезахватывается через час, `running` брошена через 15 минут,
 * `ready` пересоздаётся кнопкой «предложить заново» не чаще раза в сутки.
 */
export const THEME_BOOTSTRAP_FAILED_RETRY_MS = 60 * 60 * 1000;
export const THEME_BOOTSTRAP_RUNNING_STALE_MS = 15 * 60 * 1000;
export const THEME_BOOTSTRAP_REGENERATE_MS = 24 * 60 * 60 * 1000;

/**
 * `error_code` сбоя, после которого НИЧЕГО не оплачено — вызова модели не было:
 * `no_history` (нечего отправлять), `not_started` (Workflow не поставился,
 * провайдер или чтение истории не отработали до вызова). Такая попытка
 * перезахватывается сразу, без часа ожидания `failed`: час защищает от
 * повторной оплаты, а платить было не за что.
 */
export const THEME_BOOTSTRAP_UNPAID_FAILURE_CODES = [
  'no_history',
  'not_started',
] as const;

export function isThemeBootstrapUnpaidFailure(errorCode: string | null): boolean {
  return (
    errorCode !== null &&
    (THEME_BOOTSTRAP_UNPAID_FAILURE_CODES as readonly string[]).includes(errorCode)
  );
}

/**
 * Бюджет времени одного нажатия кнопки. Платная генерация живёт в Cloudflare
 * Workflow (`ThemeBootstrapWorkflow`, workflows-api) и переживает вызывающего
 * целиком: ни `waitUntil` вызывающего (он живёт ~30 с после ответа), ни срок
 * edge-api её не обрывают. Маршрут делает только дешёвую синхронную часть
 * (флаг, взгляд на попытку, провайдер, слот, захват) и ставит инстанс; чтение
 * истории (до 50 тыс. групп), её редакция и отбор идут в шаге Workflow `generate`
 * ПОСЛЕ захвата, прямо перед вызовом — иначе срок прокси мог бы оборвать
 * маршрут между захватом и постановкой. Пределы связаны, поэтому лежат рядом:
 *
 * - `THEME_BOOTSTRAP_PROXY_DEADLINE_MS` — сколько edge-api ждёт workflows-api:
 *   слот потолка, захват и постановка инстанса. Вышел срок — клиенту уходит
 *   состояние (обычно `running`). Модель в этот срок не входит.
 * - `THEME_BOOTSTRAP_CLIENT_TIMEOUT_MS` — сколько ждёт экран: срок edge-api плюс
 *   то, что edge-api делает ПОСЛЕ прокси, — читает состояние (D1) и доли
 *   предложения (чтение истории из Postgres, до 50 тыс. групп).
 * - `THEME_BOOTSTRAP_CALL_TIMEOUT_MS` — предел самого вызова модели.
 * - `THEME_BOOTSTRAP_STEP_TIMEOUT_MS` — предел единственного платного шага
 *   Workflow (`generate`, без повторов): чтение истории, вызов модели, запись
 *   расхода и закрытие попытки. Выше вызова с запасом (шаг не кончается раньше
 *   провайдера и не оставляет оплаченный ответ незаписанным) и ниже окна
 *   перезахвата брошенной попытки (`THEME_BOOTSTRAP_RUNNING_STALE_MS`).
 * - `THEME_BOOTSTRAP_LAST_CALL_START_MS` — самый поздний возраст ШАГА (от его
 *   начала), при котором вызов ещё начинают: предел шага за вычетом вызова
 *   (`THEME_BOOTSTRAP_CALL_TIMEOUT_MS`) и запаса `THEME_BOOTSTRAP_CALL_MARGIN_MS`
 *   (запись расхода и закрытие попытки после ответа). Вызов, начатый в этот срок,
 *   заканчивается раньше предела шага: срок шага не может прийтись на вызов
 *   модели. Опоздавшая попытка закрывается неоплаченной, вызова нет.
 * - `THEME_BOOTSTRAP_LAST_CALL_CLAIM_AGE_MS` — самый поздний возраст ЗАХВАТА, при
 *   котором вызов ещё начинают: окно перезахвата за вычетом вызова и запаса. Шаг
 *   начинается сразу за захватом, так что граница шага срабатывает раньше; эта —
 *   страховка на случай, когда движок проиграл шаг заново с опозданием (вызов,
 *   идущий, когда строку уже взял новый захват, оплатили бы дважды).
 */
export const THEME_BOOTSTRAP_PROXY_DEADLINE_MS = 30_000;
export const THEME_BOOTSTRAP_CLIENT_TIMEOUT_MS = 50_000;
export const THEME_BOOTSTRAP_CALL_TIMEOUT_MS = 120_000;
export const THEME_BOOTSTRAP_STEP_TIMEOUT_MS = 10 * 60 * 1000;
export const THEME_BOOTSTRAP_CALL_MARGIN_MS = 60_000;
export const THEME_BOOTSTRAP_LAST_CALL_START_MS =
  THEME_BOOTSTRAP_STEP_TIMEOUT_MS -
  THEME_BOOTSTRAP_CALL_TIMEOUT_MS -
  THEME_BOOTSTRAP_CALL_MARGIN_MS;
export const THEME_BOOTSTRAP_LAST_CALL_CLAIM_AGE_MS =
  THEME_BOOTSTRAP_RUNNING_STALE_MS -
  THEME_BOOTSTRAP_CALL_TIMEOUT_MS -
  THEME_BOOTSTRAP_CALL_MARGIN_MS;

/**
 * Сколько должно пройти после утверждения предложения, прежде чем ежедневная
 * чистка сотрёт его пары: дольше любого запроса утверждения (захват → создание
 * тем → возврат захвата, которому ещё нужны пары), чтобы чистка не встретила
 * утверждение в работе.
 */
export const THEME_BOOTSTRAP_APPROVAL_ERASE_GRACE_MS = 60 * 60 * 1000;

/**
 * Пары истории, сохранённые вместе с предложением, живут не дольше 30 дней:
 * это производные данные разговоров (ежедневная чистка workflows-api). Раньше
 * их стирает утверждение предложения и новая генерация.
 */
export const THEME_BOOTSTRAP_PAIRS_RETENTION_DAYS = 30;

/** Категория пары, которой нет среди категорий анализа (пусто, `missed`, мусор). */
export const THEME_BOOTSTRAP_FALLBACK_CATEGORY = 'other';

/** Строка истории, как её отдаёт SQL: сырой `topic`, категория записи, разговоров. */
export interface TopicHistoryGroup {
  topic: string;
  category: string | null;
  calls: number;
}

/** Пара `(ключ, категория)` и число разговоров с ней. */
export interface TopicPair {
  key: string;
  category: string;
  calls: number;
}

/** Хранимая пара: номер фразы — её позиция + 1. */
export type StoredTopicPair = readonly [key: string, category: string];

/**
 * Категория пары: одна из десяти категорий анализа, иначе `other` — модель
 * получает только коды, которые описывает системный промпт.
 */
export function topicPairCategory(raw: string | null | undefined): string {
  return typeof raw === 'string' &&
    Object.prototype.hasOwnProperty.call(THEME_OVERLAP_CALL_CATEGORY_TERMS, raw)
    ? raw
    : THEME_BOOTSTRAP_FALLBACK_CATEGORY;
}

function pairId(key: string, category: string): string {
  return `${category}\u0000${key}`;
}

/** Порядок пар: чаще — раньше; при равенстве — ключ, затем категория (детерминированно). */
function byCallsThenKey(a: TopicPair, b: TopicPair): number {
  if (b.calls !== a.calls) return b.calls - a.calls;
  if (a.key !== b.key) return a.key < b.key ? -1 : 1;
  if (a.category !== b.category) return a.category < b.category ? -1 : 1;
  return 0;
}

/**
 * Группы истории → пары: `topic` нормализуется, одинаковые пары сливаются со
 * сложением разговоров. Фраза без ключа (одни знаки препинания) — не пара.
 * Ключ длиннее `THEME_BOOTSTRAP_PHRASE_MAX_CHARS` обрезается по кодовым
 * точкам — и сливается с той же обрезанной фразой.
 */
export function buildTopicPairs(groups: readonly TopicHistoryGroup[]): TopicPair[] {
  const merged = new Map<string, TopicPair>();
  for (const group of groups) {
    const key = topicPairKey(group.topic);
    if (!key || !(group.calls > 0)) continue;
    const category = topicPairCategory(group.category);
    const id = pairId(key, category);
    const existing = merged.get(id);
    if (existing) existing.calls += group.calls;
    else merged.set(id, { key, category, calls: group.calls });
  }
  return [...merged.values()].sort(byCallsThenKey);
}

/** Ключ фразы истории — `normalizeThemeKey`, не длиннее фразы промпта. */
export function topicPairKey(topic: string): string {
  const key = normalizeThemeKey(topic);
  const points = Array.from(key);
  return points.length > THEME_BOOTSTRAP_PHRASE_MAX_CHARS
    ? points.slice(0, THEME_BOOTSTRAP_PHRASE_MAX_CHARS).join('').trimEnd()
    : key;
}

/**
 * Длина строки фразы в блоке промпта — с запасом на номер до `[1500]`,
 * разделитель « — » и перевод строки. Отбор и промпт меряют одним правилом.
 */
export function bootstrapLineLength(pair: Pick<TopicPair, 'key' | 'category'>): number {
  return pair.key.length + pair.category.length + 12;
}

export interface BootstrapSelection {
  /** Фразы для модели в порядке номеров: номер = позиция + 1. */
  pairs: TopicPair[];
  /** Разговоров у всех пар истории. */
  totalCalls: number;
  /** Разговоров у отобранных пар. */
  sentCalls: number;
  /** Отбор урезал историю (потолок фраз или знаков). */
  sampled: boolean;
}

/**
 * Квоты по долям: `total * weight / sum`, округление вниз, остаток — по
 * наибольшим дробным частям (при равенстве — более частой категории).
 */
function proportionalQuotas(
  weights: ReadonlyArray<readonly [string, number]>,
  total: number
): Map<string, number> {
  const sum = weights.reduce((acc, [, weight]) => acc + weight, 0);
  const quotas = new Map<string, number>();
  if (sum <= 0) return quotas;
  const remainders: Array<{ id: string; fraction: number; weight: number }> = [];
  let given = 0;
  for (const [id, weight] of weights) {
    const exact = (total * weight) / sum;
    const floor = Math.floor(exact);
    quotas.set(id, floor);
    given += floor;
    remainders.push({ id, fraction: exact - floor, weight });
  }
  remainders.sort((a, b) => b.fraction - a.fraction || b.weight - a.weight);
  for (const entry of remainders) {
    if (given >= total) break;
    quotas.set(entry.id, (quotas.get(entry.id) ?? 0) + 1);
    given += 1;
  }
  return quotas;
}

/**
 * Отбор фраз для одного вызова (§2 Р1). Вся история влезает в потолки —
 * идёт вся. Иначе — стратифицированно по категориям: квота фраз и квота
 * знаков категории пропорциональны её разговорам, внутри категории — самые
 * частые фразы первыми; недобор квот (фразы категории кончились, длинные
 * строки) добирается самыми частыми из оставшихся, пока держат оба потолка.
 */
export function selectBootstrapPhrases(
  pairs: readonly TopicPair[],
  limits: { maxPhrases: number; maxChars: number } = {
    maxPhrases: THEME_BOOTSTRAP_MAX_PHRASES,
    maxChars: THEME_BOOTSTRAP_MAX_CHARS,
  }
): BootstrapSelection {
  const ordered = [...pairs].sort(byCallsThenKey);
  const totalCalls = ordered.reduce((sum, pair) => sum + pair.calls, 0);
  const allChars = ordered.reduce((sum, pair) => sum + bootstrapLineLength(pair), 0);
  if (ordered.length <= limits.maxPhrases && allChars <= limits.maxChars) {
    return { pairs: ordered, totalCalls, sentCalls: totalCalls, sampled: false };
  }

  const byCategory = new Map<string, TopicPair[]>();
  for (const pair of ordered) {
    const list = byCategory.get(pair.category) ?? [];
    list.push(pair);
    byCategory.set(pair.category, list);
  }
  const weights = [...byCategory.entries()]
    .map(([category, list]) => [category, list.reduce((s, p) => s + p.calls, 0)] as const)
    .sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1));
  const phraseQuota = proportionalQuotas(weights, limits.maxPhrases);
  const charQuota = proportionalQuotas(weights, limits.maxChars);

  const chosen = new Set<TopicPair>();
  let count = 0;
  let chars = 0;
  for (const [category] of weights) {
    let catCount = 0;
    let catChars = 0;
    for (const pair of byCategory.get(category) ?? []) {
      const length = bootstrapLineLength(pair);
      if (catCount >= (phraseQuota.get(category) ?? 0)) break;
      if (catChars + length > (charQuota.get(category) ?? 0)) continue;
      chosen.add(pair);
      catCount += 1;
      catChars += length;
    }
    count += catCount;
    chars += catChars;
  }
  for (const pair of ordered) {
    if (count >= limits.maxPhrases) break;
    if (chosen.has(pair)) continue;
    const length = bootstrapLineLength(pair);
    if (chars + length > limits.maxChars) continue;
    chosen.add(pair);
    count += 1;
    chars += length;
  }

  const selected = ordered.filter((pair) => chosen.has(pair));
  return {
    pairs: selected,
    totalCalls,
    sentCalls: selected.reduce((sum, pair) => sum + pair.calls, 0),
    sampled: true,
  };
}

/** Тема предложения, как её видит подсчёт: номера фраз (1-based). */
export interface ProposalThemePhrases {
  phrases: readonly number[];
}

export interface BootstrapShares {
  /** Разговоры истории окна (знаменатель всех долей). */
  totalCalls: number;
  /** Разговоры, чья пара ушла в модель («предложено по N % разговоров»). */
  coveredCalls: number;
  /** Разговоры хотя бы одной темы — объединение, без двойного счёта. */
  themedCalls: number;
  /** Разговоры каждой темы — в порядке тем. */
  themeCalls: number[];
}

/**
 * Доли тем по сохранённым парам (§2 Р1): группы истории ТЕКУЩЕГО окна
 * сводятся теми же правилами (`topicPairKey`, `topicPairCategory`), пара
 * находит свой номер по сохранённому списку — не по позиции в новой
 * выборке: удалённый с тех пор разговор не сдвигает номера, он просто
 * выпадает из счёта. Номер вне списка — не считается.
 *
 * `sendable` — то же правило, по которому фразы уходили в модель
 * (`sendableGroup`, `voc/theme-bootstrap-redaction`: сырая фраза И её ключ):
 * удержанная редакцией группа не вошла в пары, и совпадение её ключа с ключом
 * ушедшей фразы — случайность нормализации, а не её разговоры. Такая группа остаётся в знаменателе
 * (`totalCalls`: разговор с темой в окне есть) и не покрыта.
 */
export function computeBootstrapShares(
  groups: readonly TopicHistoryGroup[],
  storedPairs: readonly StoredTopicPair[],
  themes: readonly ProposalThemePhrases[],
  sendable: (group: TopicHistoryGroup) => boolean
): BootstrapShares {
  const indexOf = new Map<string, number>();
  storedPairs.forEach(([key, category], position) => {
    indexOf.set(pairId(key, category), position + 1);
  });
  const callsByIndex = new Map<number, number>();
  let totalCalls = 0;
  let coveredCalls = 0;
  for (const group of groups) {
    const key = topicPairKey(group.topic);
    if (!key || !(group.calls > 0)) continue;
    totalCalls += group.calls;
    // The index first: `sendable` re-runs the redactor over the raw phrase, and
    // only a group that could be counted needs its verdict.
    const index = indexOf.get(pairId(key, topicPairCategory(group.category)));
    if (index === undefined || !sendable(group)) continue;
    coveredCalls += group.calls;
    callsByIndex.set(index, (callsByIndex.get(index) ?? 0) + group.calls);
  }
  const sumOf = (indexes: Iterable<number>): number => {
    let sum = 0;
    for (const index of indexes) sum += callsByIndex.get(index) ?? 0;
    return sum;
  };
  const union = new Set<number>();
  const themeCalls = themes.map((theme) => {
    const own = new Set(theme.phrases);
    for (const index of own) union.add(index);
    return sumOf(own);
  });
  return { totalCalls, coveredCalls, themedCalls: sumOf(union), themeCalls };
}
